import React, { useState, useEffect } from 'react';
import { CalendarIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { formatForInput, convertLocalToUTC, convertDateOnlyToUTC, convertUTCToDateOnly } from '../../utils/dateFormat';
import { CustomDatePicker } from './CustomDatePicker';
import { EnhancedDateTimePicker } from './EnhancedDateTimePicker';

interface DateInputProps {
  value?: string;
  onChange?: (value: string) => void;
  onValidationChange?: (isValid: boolean) => void;
  label?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  error?: string;
  className?: string;
  minDate?: string;
  maxDate?: string;
  showAge?: boolean; // For date of birth, show calculated age
  showTime?: boolean; // For datetime input (appointments)
  isDateOnly?: boolean; // For date-only fields like DOB (no timezone conversion)
}

export const DateInput: React.FC<DateInputProps> = ({
  value = '',
  onChange,
  onValidationChange,
  label = 'Date',
  placeholder,
  required = false,
  disabled = false,
  error,
  className = '',
  minDate,
  maxDate,
  showAge = false,
  showTime = false,
  isDateOnly = false,
}) => {
  const [isValid, setIsValid] = useState(true);
  const [isTouched, setIsTouched] = useState(false);
  const [validationMessage, setValidationMessage] = useState('');
  const [calculatedAge, setCalculatedAge] = useState<number | null>(null);

  // Format date consistently for HTML input (YYYY-MM-DD or YYYY-MM-DDTHH:mm)
  const formatDateForInput = (dateString: string): string => {
    if (!dateString) return '';
    
    if (isDateOnly) {
      // For date-only fields, use the utility function that preserves the date
      return convertUTCToDateOnly(dateString);
    } else {
      // For datetime fields, use the utility function that handles timezone conversion
      return formatForInput(dateString, showTime);
    }
  };

  // Calculate age from date of birth
  const calculateAge = (birthDate: string): number | null => {
    if (!birthDate) return null;
    
    try {
      const birth = new Date(birthDate);
      const today = new Date();
      
      if (isNaN(birth.getTime()) || birth > today) return null;
      
      let age = today.getFullYear() - birth.getFullYear();
      const monthDiff = today.getMonth() - birth.getMonth();
      
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
        age--;
      }
      
      return Math.max(0, age);
    } catch {
      return null;
    }
  };

  const validateDate = (dateString: string): { isValid: boolean; message: string } => {
    if (!dateString.trim()) {
      return { isValid: !required, message: required ? 'Date is required' : '' };
    }

    try {
      const date = new Date(dateString);
      
      if (isNaN(date.getTime())) {
        return { isValid: false, message: 'Please enter a valid date' };
      }

      const today = new Date();
      today.setHours(23, 59, 59, 999); // End of today

      // Check if date is in the future (for birth dates)
      if (showAge && date > today) {
        return { isValid: false, message: 'Date of birth cannot be in the future' };
      }

      // Check minimum date. A date-only field compares calendar days, so a minDate with a time
      // later today does not reject today (AMP-146); a date-and-time field compares instants.
      if (minDate) {
        const minDateTime = new Date(minDate);
        // Date-only values are stored as UTC midnight of their day (convertDateOnlyToUTC), so their
        // calendar day is read from the UTC parts; minDate is an instant read in local time.
        const tooEarly = showTime
          ? date < minDateTime
          : Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()) <
            Date.UTC(minDateTime.getFullYear(), minDateTime.getMonth(), minDateTime.getDate());
        if (tooEarly) {
          return {
            isValid: false,
            message: `Date cannot be before ${formatDisplayDate(minDate)}`
          };
        }
      }

      // Check maximum date
      if (maxDate) {
        const maxDateTime = new Date(maxDate);
        if (date > maxDateTime) {
          return { 
            isValid: false, 
            message: `Date must be before ${formatDisplayDate(maxDate)}` 
          };
        }
      }

      // Age-specific validations for birth dates
      if (showAge) {
        const age = calculateAge(dateString);
        if (age !== null) {
          if (age > 150) {
            return { isValid: false, message: 'Please verify this date of birth' };
          }
          if (age === 0) {
            return { isValid: true, message: 'Infant (less than 1 year old)' };
          }
        }
      }

      return { isValid: true, message: '' };
    } catch {
      return { isValid: false, message: 'Please enter a valid date' };
    }
  };

  // Format date for display (e.g., "01/15/1990")
  const formatDisplayDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  useEffect(() => {
    if (isTouched || value) {
      const validation = validateDate(value);
      setIsValid(validation.isValid);
      setValidationMessage(validation.message);
      onValidationChange?.(validation.isValid);

      // Calculate age for birth dates
      if (showAge) {
        setCalculatedAge(calculateAge(value));
      }
    }
  }, [value, isTouched, required, minDate, maxDate, showAge]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    
    if (!newValue) {
      onChange?.('');
      return;
    }
    
    // Convert the input value to the appropriate format for the server
    let convertedValue: string;
    
    if (isDateOnly) {
      // For date-only fields, convert to UTC preserving the date
      convertedValue = convertDateOnlyToUTC(newValue);
    } else if (showTime) {
      // For datetime fields, convert local time to UTC
      convertedValue = convertLocalToUTC(newValue);
    } else {
      // For regular date fields, convert to UTC preserving the date
      convertedValue = convertDateOnlyToUTC(newValue);
    }
    
    onChange?.(convertedValue);
  };

  const handleBlur = () => {
    setIsTouched(true);
  };

  const handleFocus = () => {
    if (!isTouched) {
      setIsTouched(true);
    }
  };

  const getInputClassName = () => {
    let baseClassName = `
      w-full px-3 py-2 pl-10 border rounded-md
      focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500
      disabled:opacity-50 disabled:cursor-not-allowed
      transition-colors duration-200
    `;

    if (error) {
      baseClassName += ' border-red-300 bg-red-50';
    } else if (isTouched && !isValid) {
      baseClassName += ' border-red-300 bg-red-50';
    } else if (isTouched && isValid && value) {
      baseClassName += ' border-green-300 bg-green-50';
    } else {
      baseClassName += ' border-gray-300';
    }

    return baseClassName;
  };

  // Set reasonable default min/max dates if not provided
  const defaultMinDate = showAge ? '1900-01-01' : undefined;
  const defaultMaxDate = showAge ? new Date().toISOString().split('T')[0] : undefined;

  // Use custom date picker for date-only fields (better UX for birth dates)
  if (isDateOnly) {
    return (
      <CustomDatePicker
        value={value}
        onChange={onChange}
        onValidationChange={onValidationChange}
        label={label}
        placeholder={placeholder}
        required={required}
        disabled={disabled}
        error={error}
        className={className}
        minDate={minDate || defaultMinDate}
        maxDate={maxDate || defaultMaxDate}
        showAge={showAge}
      />
    );
  }

  // Use enhanced datetime picker for datetime fields (appointments)
  if (showTime) {
    return (
      <EnhancedDateTimePicker
        value={value}
        onChange={onChange}
        onValidationChange={onValidationChange}
        label={label}
        placeholder={placeholder}
        required={required}
        disabled={disabled}
        error={error}
        className={className}
        minDate={minDate}
        maxDate={maxDate}
      />
    );
  }

  // Use native input for datetime fields (appointments)
  return (
    <div className={`space-y-1 ${className}`}>
      {label && (
        <label className="block text-sm font-medium text-gray-700">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      
      <div className="relative">
        {/* Calendar Icon */}
        <div className="absolute inset-y-0 left-0 flex items-center pl-3">
          <CalendarIcon className="w-5 h-5 text-gray-400" />
        </div>
        
        <input
          type={showTime ? "datetime-local" : "date"}
          value={formatDateForInput(value)}
          onChange={handleChange}
          onBlur={handleBlur}
          onFocus={handleFocus}
          placeholder={placeholder}
          disabled={disabled}
          required={required}
          min={minDate || defaultMinDate}
          max={maxDate || defaultMaxDate}
          className={getInputClassName()}
        />
      </div>

      {/* Age Display */}
      {showAge && calculatedAge !== null && isValid && value && (
        <div className="text-sm text-gray-600">
          Age: {calculatedAge} year{calculatedAge !== 1 ? 's' : ''} old
        </div>
      )}
      
      {/* Error Message */}
      {error && (
        <p className="text-sm text-red-600 flex items-center">
          <ExclamationCircleIcon className="w-4 h-4 mr-1" />
          {error}
        </p>
      )}
      
      {/* Validation Message */}
      {!error && isTouched && validationMessage && (
        <p className={`text-sm flex items-center ${
          isValid ? 'text-blue-600' : 'text-red-600'
        }`}>
          {isValid ? (
            <span className="text-blue-500 mr-1">ℹ️</span>
          ) : (
            <ExclamationCircleIcon className="w-4 h-4 mr-1" />
          )}
          {validationMessage}
        </p>
      )}
      
      {/* Format Help */}
      {!value && !isTouched && (
        <p className="text-xs text-gray-500">
          Use the date picker or enter in YYYY-MM-DD format
        </p>
      )}
    </div>
  );
};